import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { Bar } from '../../../../assets/charting_library/charting_library';
import { DataSharingService } from '../data-sharing/data-sharing.service';
// import moment from 'moment';
import moment from 'moment-timezone';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environment/environment';

interface SubscriptionHandler {
  id: string;
  callback: (bar: Bar) => void;
}

interface SubscriptionItem {
  subscriberUID: string;
  resolution: string;
  lastDailyBar: Bar;
  handlers: SubscriptionHandler[];
}

@Injectable({
  providedIn: 'root',
})
export class CommonService {
  private isBrowser = false;
  private getInitOrders = new BehaviorSubject<string>('');
  getInitOrders$ = this.getInitOrders.asObservable();
  orderEngineUrl = environment.apiUrlOrderEngine;
  channelToSubscription: Map<string, SubscriptionItem> = new Map();
  currentSelectedSymbol: any;
  currentSelectedSubscriberId: any;
  lotSize: any = {
    BTC: 0.001, // 1 Lot BTC = 0.001 BTC
    ETH: 0.01, // 1 Lot ETH = 0.01 ETH
  };

  constructor(
    private dataSharingService: DataSharingService,
    @Inject(PLATFORM_ID) platformId: Object,
    private http: HttpClient,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);

    // this.dataSharingService.futuresData$
    // .subscribe((_d: any) => {
    //   this.createNewCandle(_d.futuresData)
    // })
    // this.dataSharingService.optionsData$
    // .subscribe((_d: any) => {
    //   this.createNewCandle(_d.optionData)
    // })
    combineLatest([
      this.dataSharingService.futuresData$,
      this.dataSharingService.optionsData$,
    ]).subscribe(([futures, options]) => {
      const futuresData = Array.isArray(futures?.futuresData)
        ? futures.futuresData
        : [];

      const optionData = Array.isArray(options?.optionData)
        ? options.optionData
        : [];
      let data = [...futuresData, ...optionData]; // Merging both arrays
      this.createNewCandle(data);
    });
    this.getAllInitOrders().subscribe((res: any) => {
      console.log('Init Orders Response:', res.data);
      this.sendInitOrders(res.data);
    });
  }

  getAllInitOrders() {
    return this.http.get(`${this.orderEngineUrl}/orders/getAllInitOrders`);
  }

  calculateLongMargin(
    optionMarkPrice: any,
    asset: any,
    contracts: any,
    quantityType: any,
    leverage: any,
  ) {
    if (!leverage || leverage <= 0) {
      throw new Error('Leverage must be greater than zero');
    }

    let premium, buffer, margin;

    if (quantityType != 'usd' && quantityType != 'lot') {
      premium = optionMarkPrice * contracts;
    } else if (quantityType == 'lot') {
      premium = optionMarkPrice * this.lotSize[asset] * contracts;
    } else {
      throw new Error('Invalid quantity type');
    }

    buffer = 0.055 * premium; // Approx 5.5% buffer
    margin = premium + buffer; // Apply leverage

    return margin; // Final long margin requirement
  }

  calculateMarginRequired(order: any) {
    let { tradePrice, size } = order;
    if (!order || !tradePrice || !order.leverage || !size) {
      console.error('Invalid parameters for margin calculation');
      return null;
    }
    const leverage = parseFloat(order.leverage);
    tradePrice = parseFloat(tradePrice);
    let orderSizeUSD = 0;
    let marginRequired = 0;
    // Convert order size to USD based on quantityType
    if (order.quantityType != 'usd' && order.quantityType != 'lot') {
      const orderSizeUSD = size * tradePrice;
      marginRequired = orderSizeUSD / leverage;
    }
    if (order.quantityType == 'lot') {
      orderSizeUSD = order.lotSize * size * tradePrice;
      marginRequired = orderSizeUSD / leverage;
    }
    // Round off to a maximum of 2 decimal places
    return parseFloat(marginRequired.toFixed(2));
  }

  calculateFuturesMargin(order: any) {
    // Ensure valid inputs
    let { tradePrice, contracts } = order;
    if (!order || !tradePrice || !order.leverage || !order.contracts) {
      console.error('Invalid parameters for margin calculation');
      return null;
    }
    const leverage = parseFloat(order.leverage);
    tradePrice = parseFloat(tradePrice);
    let orderSizeUSD = 0;
    let marginRequired = 0;
    // Convert order size to USD based on quantityType
    if (order.quantityType != 'usd' && order.quantityType != 'lot') {
      const orderSizeUSD = contracts * tradePrice;
      marginRequired = orderSizeUSD / leverage;
    }
    if (order.quantityType == 'lot') {
      orderSizeUSD = order.lotSize * contracts * tradePrice;
      marginRequired = orderSizeUSD / leverage;
    }
    // Round off to a maximum of 2 decimal places
    return parseFloat(marginRequired.toFixed(2));
  }

  calculateOptionsMargin(data: any) {
    const {
      symbol,
      optionType,
      indexPrice,
      optionMarkPrice,
      contracts,
      quantityType,
      leverage,
    } = data;

    let initialMargin, maintenanceMargin;
    if (optionType === 'longCall' || optionType === 'longPut') {
      initialMargin = this.calculateLongMargin(
        optionMarkPrice,
        symbol,
        contracts,
        quantityType,
        leverage,
      );
      maintenanceMargin = 0;
    } else if (optionType === 'shortCall' || optionType === 'shortPut') {
      // For short options, calculate initial and maintenance margins
      initialMargin = this.calculateMarginRequired({
        leverage: leverage,
        size: contracts,
        tradePrice: indexPrice,
        lotSize: this.lotSize[symbol],
        quantityType: quantityType,
      });
      return initialMargin;
      // maintenanceMargin = this.calculateMaintenanceMargin(symbol, optionMarkPrice, contracts, indexPrice);
    } else {
      return { error: 'Invalid option type' };
    }

    return initialMargin.toFixed(2);
  }

  calculateMargin(data: any, orders: any): any {
    let margin;
    if (orders.length) {
      // let order = orders.filter((_d: any) => _d.symbol == data.contractName)[0]
      let groupMatchPositions = orders
        .filter((order: any) => order.symbol === data.contractName)
        .reduce(
          (acc: any, order: any) => {
            if (order.positionSide === 'buy') {
              acc.buySize += Number(order.size);
            } else if (order.positionSide === 'sell') {
              acc.sellSize += Number(order.size);
            }
            return acc;
          },
          { buySize: 0, sellSize: 0 },
        );
      // if (order) {
      //   if (order.positionSide != data.positionSide) {
      //     if (data.contracts <= order.size) {
      //       margin = 0;
      //       return margin
      //     } else if (data.contracts > order.size) {
      //       data.contracts = Number(data.contracts) - Number(order.size);
      //     }
      //   }
      // }
      // console.log("check the buy and sell orders...", groupMatchPositions, data.positionSide)
      if (groupMatchPositions.buySize && data.positionSide == 'sell') {
        if (groupMatchPositions.sellSize <= groupMatchPositions.buySize) {
          let remainingOrders =
            groupMatchPositions.buySize - groupMatchPositions.sellSize;
          if (data.contracts <= remainingOrders) {
            margin = 0;
            return margin;
          } else if (data.contracts > remainingOrders) {
            data.contracts = Number(data.contracts) - Number(remainingOrders);
          }
        }
      }
      if (groupMatchPositions.sellSize && data.positionSide == 'buy') {
        if (groupMatchPositions.buySize <= groupMatchPositions.sellSize) {
          let remainingOrders =
            groupMatchPositions.sellSize - groupMatchPositions.buySize;
          if (data.contracts <= remainingOrders) {
            margin = 0;
            return margin;
          } else if (data.contracts > remainingOrders) {
            data.contracts = Number(data.contracts) - Number(remainingOrders);
          }
        }
      }
    }
    if (data.contractType == 'futures') {
      margin = this.calculateFuturesMargin(data);
    } else {
      margin = this.calculateOptionsMargin(data);
    }
    return margin;
  }

  subscribeOnStream(
    symbolInfo: any,
    resolution: string,
    onRealtimeCallback: (bar: Bar) => void,
    subscriberUID: string,
    onResetCacheNeededCallback: () => void,
    lastDailyBar: Bar,
  ): void {
    const parsedSymbol = this.parseFullSymbol(symbolInfo.full_name);
    // subscriberUID = symbolInfo.base_name[0]
    const channelString: any = subscriberUID; //'C-BTC-84000-280325'//`0~${parsedSymbol?.exchange}~${parsedSymbol?.fromSymbol}~${parsedSymbol?.toSymbol}`;

    const handler: SubscriptionHandler = {
      id: subscriberUID,
      callback: onRealtimeCallback,
    };

    let subscriptionItem = this.channelToSubscription.get(channelString);
    if (subscriptionItem) {
      // Already subscribed, just add the new handler
      subscriptionItem.handlers.push(handler);
      return;
    }

    // Create a new subscription item
    subscriptionItem = {
      subscriberUID,
      resolution,
      lastDailyBar,
      handlers: [handler],
    };

    this.channelToSubscription.set(channelString, subscriptionItem);

    // this.currentSelectedSymbol = symbolInfo.base_name[0]
    this.currentSelectedSubscriberId = subscriberUID;
    // const subRequest = {
    //   action: 'SubAdd',
    //   subs: [channelString],
    // };

    // socket.send(JSON.stringify(subRequest));
  }

  parseFullSymbol(fullSymbol: string) {
    const match = fullSymbol.match(/^(\w+):(\w+)\/(\w+)$/);

    if (!match) {
      return null;
    }

    return {
      exchange: match[1],
      fromSymbol: match[2],
      toSymbol: match[3],
    };
  }

  createNewCandle(event: any) {
    if (this.isBrowser) {
      // const data = JSON.parse(event.data);
      let contractType = localStorage.getItem('contractType');
      this.currentSelectedSymbol = localStorage.getItem('contractName');
      let chartType = localStorage.getItem('chartType');
      let data: any;
      if (contractType == 'futures') {
        data = event.find((_d: any) => _d.s == this.currentSelectedSymbol);
      } else {
        data = event.find((_d: any) => _d.symbol == this.currentSelectedSymbol);
      }
      // console.log('check the candle creation...', data, contractType, this.currentSelectedSymbol)
      // let {
      // 	T: tradeTimeStr,
      // 	p: tradePriceStr,
      // } = data;
      let tradePriceStr: any;
      if (chartType == 'mark') {
        tradePriceStr = contractType == 'futures' ? data.p : data?.markPrice; //data.markPrice
      } else {
        tradePriceStr = contractType == 'futures' ? data.i : data?.indexPrice; //data.markPrice
      }
      let tradeTimeStr: any = moment().utc().valueOf() + 19800000; //moment().utc().valueOf();//data.time
      // console.log('[socket] Message:', tradeTimeStr);
      const tradePrice = parseFloat(tradePriceStr);
      // const tradeTime = parseInt(tradeTimeStr) * 1000;
      const tradeTime = parseInt(tradeTimeStr); // * 1000;  // Convert UTC to IST
      const channelString = this.currentSelectedSubscriberId; //`0~${exchange}~${fromSymbol}~${toSymbol}`;
      const subscriptionItem = this.channelToSubscription.get(channelString);

      if (!subscriptionItem) {
        return;
      }

      const lastDailyBar = subscriptionItem.lastDailyBar;
      let timeFrame = parseInt(subscriptionItem.resolution);
      const nextDailyBarTime = this.getNextCandleTimestamp(
        lastDailyBar.time,
        timeFrame,
      );
      const nextBartime = new Date(nextDailyBarTime).toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata',
      });
      const tradeTimeCurrent = new Date(tradeTime).toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata',
      });
      // console.log("check next bar time...", nextBartime, tradeTimeCurrent, timeFrame, channelString)
      let bar: Bar;
      if (tradeTime >= nextDailyBarTime) {
        bar = {
          time: nextDailyBarTime,
          open: tradePrice,
          high: tradePrice,
          low: tradePrice,
          close: tradePrice,
        };
        // console.log('[socket] Generate new bar', bar);
      } else {
        bar = {
          ...lastDailyBar,
          high: Math.max(lastDailyBar.high, tradePrice),
          low: Math.min(lastDailyBar.low, tradePrice),
          close: tradePrice,
        };
        // console.log('[socket] Update the latest bar by price',  bar);
      }

      subscriptionItem.lastDailyBar = bar;

      // Notify all subscribers
      subscriptionItem.handlers.forEach((handler) => handler.callback(bar));
    }
  }

  // getNextCandleTimestamp(lastTimestamp: any, intervalMinutes: number): number {
  //   const lastDate = new Date(lastTimestamp);

  //   // Reset seconds and milliseconds
  //   lastDate.setUTCSeconds(0, 0);

  //   // Get the current minutes
  //   const currentMinutes = lastDate.getUTCMinutes();

  //   // Find the next valid interval by rounding up
  //   const nextMinutes =
  //     Math.ceil((currentMinutes + 1) / intervalMinutes) * intervalMinutes;

  //   lastDate.setUTCMinutes(nextMinutes);

  //   return lastDate.getTime(); // Return in milliseconds
  // }

  getNextCandleTimestamp(
    lastTimestamp: number,
    intervalMinutes: number,
  ): number {
    const intervalMs = intervalMinutes * 60 * 1000;
    return lastTimestamp + intervalMs;
  }

  clearSubscriptions() {
    this.channelToSubscription.clear();
    this.currentSelectedSubscriberId = '';
    this.currentSelectedSymbol = '';
  }

  unsubscribeFromStream(subscriberUID: string): void {
    for (const channelString of this.channelToSubscription.keys()) {
      const subscriptionItem = this.channelToSubscription.get(channelString);

      if (!subscriptionItem) continue;

      const handlerIndex = subscriptionItem.handlers.findIndex(
        (handler) => handler.id === subscriberUID,
      );

      if (handlerIndex !== -1) {
        // Remove the handler from the list
        subscriptionItem.handlers.splice(handlerIndex, 1);
        if (subscriptionItem.handlers.length === 0) {
          // Unsubscribe if no more handlers

          // const subRequest = {
          // 	action: 'SubRemove',
          // 	subs: [channelString],
          // };

          // socket.send(JSON.stringify(subRequest));
          this.channelToSubscription.delete(channelString);
          break;
        }
      }
    }
  }

  loadDigioScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.isBrowser) {
        const script = document.createElement('script');
        script.src = 'https://app.digio.in/sdk/v11/digio.js';
        script.type = 'text/javascript';
        script.onload = () => resolve();
        script.onerror = () => reject();
        document.body.appendChild(script);
      }
    });
  }

  sendInitOrders(data: string) {
    this.getInitOrders.next(data);
  }

  futuresEpsilonArray = [
    {
      symbol: 'BTCUSDT',
      epsilon: 1,
      lotSize: 0.001,
      lotUnit: 'BTC',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/1.png',
      leverage: 200,
      maintainanceMargin: 0.25,
    },
    {
      symbol: 'ETHUSDT',
      epsilon: 2,
      lotSize: 0.01,
      lotUnit: 'ETH',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/1027.png',
      leverage: 200,
      maintainanceMargin: 0.25,
    },
    {
      symbol: 'SOLUSDT',
      epsilon: 2,
      lotSize: 0.01,
      lotUnit: 'SOL',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/5426.png',
      leverage: 100,
      maintainanceMargin: 0.5,
    },
    {
      symbol: 'XRPUSDT',
      epsilon: 4,
      lotSize: 1,
      lotUnit: 'XRP',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/52.png',
      leverage: 100,
      maintainanceMargin: 0.5,
    },
    {
      symbol: 'TRUMPUSDT',
      epsilon: 2,
      lotSize: 0.1,
      lotUnit: 'TRUMP',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/35336.png',
      leverage: 20,
      maintainanceMargin: 2.5,
    },
    {
      symbol: 'DOGEUSDT',
      epsilon: 6,
      lotSize: 100,
      lotUnit: 'DOGE',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/74.png',
      leverage: 100,
      maintainanceMargin: 0.5,
    },
    {
      symbol: 'FARTCOINUSDT',
      epsilon: 3,
      lotSize: 1,
      lotUnit: 'FARTCOIN',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/33597.png',
      leverage: 20,
      maintainanceMargin: 2.5,
    },
    {
      symbol: 'LINKUSDT',
      epsilon: 3,
      lotSize: 1,
      lotUnit: 'LINK',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/1975.png',
      leverage: 100,
      maintainanceMargin: 0.5,
    },
    {
      symbol: 'HIVEUSDT',
      epsilon: 5,
      lotSize: 10,
      lotUnit: 'HIVE',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/5370.png',
      leverage: 20,
      maintainanceMargin: 2.5,
    },
    {
      symbol: 'AVAXUSDT',
      epsilon: 3,
      lotSize: 1,
      lotUnit: 'AVAX',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/5805.png',
      leverage: 100,
      maintainanceMargin: 0.5,
    },
    {
      symbol: 'BNBUSDT',
      epsilon: 2,
      lotSize: 0.1,
      lotUnit: 'BNB',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/1839.png',
      leverage: 100,
      maintainanceMargin: 0.5,
    },
    {
      symbol: 'LTCUSDT',
      epsilon: 2,
      lotSize: 0.1,
      lotUnit: 'LTC',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/2.png',
      leverage: 100,
      maintainanceMargin: 0.5,
    },
    {
      symbol: 'AAVEUSDT',
      epsilon: 2,
      lotSize: 1,
      lotUnit: 'AAVE',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/7278.png',
      leverage: 50,
      maintainanceMargin: 1.0,
    },
    {
      symbol: 'ADAUSDT',
      epsilon: 4,
      lotSize: 1,
      lotUnit: 'ADA',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/2010.png',
      leverage: 100,
      maintainanceMargin: 0.5,
    },
    {
      symbol: 'ALGOUSDT',
      epsilon: 4,
      lotSize: 1,
      lotUnit: 'ALGO',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/4030.png',
      leverage: 100,
      maintainanceMargin: 0.5,
    },
    {
      symbol: 'BCHUSDT',
      epsilon: 2,
      lotSize: 0.01,
      lotUnit: 'BCH',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/1831.png',
      leverage: 100,
      maintainanceMargin: 0.5,
    },
    {
      symbol: 'AIXBTUSDT',
      epsilon: 5,
      lotSize: 10,
      lotUnit: 'AIXBT',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/34103.png',
      leverage: 20,
      maintainanceMargin: 2.5,
    },
    {
      symbol: 'JUPUSDT',
      epsilon: 4,
      lotSize: 10,
      lotUnit: 'JUP',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/29210.png',
      leverage: 20,
      maintainanceMargin: 2.5,
    },
    {
      symbol: 'MELANIAUSDT',
      epsilon: 2,
      lotSize: 1,
      lotUnit: 'MELANIA',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/35347.png',
      leverage: 20,
      maintainanceMargin: 2.5,
    },
    {
      symbol: 'ONDOUSDT',
      epsilon: 4,
      lotSize: 10,
      lotUnit: 'ONDO',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/21159.png',
      leverage: 20,
      maintainanceMargin: 2.5,
    },
    {
      symbol: 'DOTUSDT',
      epsilon: 3,
      lotSize: 1,
      lotUnit: 'DOT',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/6636.png',
      leverage: 100,
      maintainanceMargin: 0.5,
    },
    {
      symbol: 'GOATUSDT',
      epsilon: 4,
      lotSize: 10,
      lotUnit: 'GOAT',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/33440.png',
      leverage: 20,
      maintainanceMargin: 2.5,
    },
    {
      symbol: 'UNIUSDT',
      epsilon: 2,
      lotSize: 1,
      lotUnit: 'UNI',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/7083.png',
      leverage: 100,
      maintainanceMargin: 0.5,
    },
    {
      symbol: 'LDOUSDT',
      epsilon: 3,
      lotSize: 1,
      lotUnit: 'LDO',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/8000.png',
      leverage: 20,
      maintainanceMargin: 2.5,
    },
    {
      symbol: 'POPCATUSDT',
      epsilon: 4,
      lotSize: 1,
      lotUnit: 'POPCAT',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/28782.png',
      leverage: 20,
      maintainanceMargin: 2.5,
    },
    {
      symbol: 'ETCUSDT',
      epsilon: 2,
      lotSize: 0.1,
      lotUnit: 'ETC',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/1321.png',
      leverage: 50,
      maintainanceMargin: 1.0,
    },
    {
      symbol: 'SPXUSDT',
      epsilon: 4,
      lotSize: 10,
      lotUnit: 'SPX',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/28081.png',
      leverage: 20,
      maintainanceMargin: 2.5,
    },
    {
      symbol: 'SWARMSUSDT',
      epsilon: 4,
      lotSize: 10,
      lotUnit: 'SWARM',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/607.png',
      leverage: 20,
      maintainanceMargin: 2.5,
    },
    {
      symbol: 'SEIUSDT',
      epsilon: 4,
      lotSize: 10,
      lotUnit: 'SEI',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/23149.png',
      leverage: 20,
      maintainanceMargin: 2.5,
    },
    {
      symbol: 'EIGENUSDT',
      epsilon: 3,
      lotSize: 1,
      lotUnit: 'EIGEN',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/30494.png',
      leverage: 20,
      maintainanceMargin: 2.5,
    },
    {
      symbol: 'VINEUSDT',
      epsilon: 5,
      lotSize: 10,
      lotUnit: 'VINE',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/35421.png',
      leverage: 20,
      maintainanceMargin: 2.5,
    },
    {
      symbol: 'WLDUSDT',
      epsilon: 4,
      lotSize: 1,
      lotUnit: 'WLD',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/13502.png',
      leverage: 20,
      maintainanceMargin: 2.5,
    },
    {
      symbol: 'BIOUSDT',
      epsilon: 4,
      lotSize: 10,
      lotUnit: 'BIO',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/34812.png',
      leverage: 20,
      maintainanceMargin: 2.5,
    },
    {
      symbol: 'PENGUUSDT',
      epsilon: 6,
      lotSize: 100,
      lotUnit: 'PENGU',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/34466.png',
      leverage: 20,
      maintainanceMargin: 2.5,
    },
    {
      symbol: 'HBARUSDT',
      epsilon: 5,
      lotSize: 1,
      lotUnit: 'HBAR',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/4642.png',
      leverage: 20,
      maintainanceMargin: 2.5,
    },
    {
      symbol: 'ATOMUSDT',
      epsilon: 3,
      lotSize: 1,
      lotUnit: 'ATOM',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/3794.png',
      leverage: 100,
      maintainanceMargin: 0.5,
    },
    {
      symbol: 'TRXUSDT',
      epsilon: 5,
      lotSize: 1,
      lotUnit: 'TRX',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/1958.png',
      leverage: 50,
      maintainanceMargin: 1.0,
    },
    {
      symbol: 'ACTUSDT',
      epsilon: 4,
      lotSize: 10,
      lotUnit: 'ACT',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/33566.png',
      leverage: 20,
      maintainanceMargin: 2.5,
    },
    {
      symbol: 'GRIFFAINUSDT',
      epsilon: 5,
      lotSize: 10,
      lotUnit: 'GRIFFAIN',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/34792.png',
      leverage: 20,
      maintainanceMargin: 2.5,
    },
    {
      symbol: 'ARCUSDT',
      epsilon: 5,
      lotSize: 10,
      lotUnit: 'ARC',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/34926.png',
      leverage: 20,
      maintainanceMargin: 2.5,
    },
    {
      symbol: 'SHIBUSDT',
      epsilon: 8,
      lotSize: 10000,
      lotUnit: 'SHIB',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/5994.png',
      leverage: 20,
      maintainanceMargin: 2.5,
    },
    {
      symbol: 'GALAUSDT',
      epsilon: 5,
      lotSize: 100,
      lotUnit: 'GALA',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/7080.png',
      leverage: 20,
      maintainanceMargin: 2.5,
    },
    {
      symbol: 'MOVEUSDT',
      epsilon: 4,
      lotSize: 10,
      lotUnit: 'MOVE',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/32452.png',
      leverage: 20,
      maintainanceMargin: 2.5,
    },
    {
      symbol: 'SONICUSDT',
      epsilon: 4,
      lotSize: 10,
      lotUnit: 'SONIC',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/35051.png',
      leverage: 20,
      maintainanceMargin: 2.5,
    },
    {
      symbol: 'MEMEUSDT',
      epsilon: 6,
      lotSize: 100,
      lotUnit: 'MEME',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/28301.png',
      leverage: 20,
      maintainanceMargin: 2.5,
    },
    {
      symbol: 'POLUSDT',
      epsilon: 4,
      lotSize: 10,
      lotUnit: 'POL',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/28321.png',
      leverage: 50,
      maintainanceMargin: 1.0,
    },
    {
      symbol: 'AVAAIUSDT',
      epsilon: 5,
      lotSize: 100,
      lotUnit: 'AVAAI',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/35156.png',
      leverage: 20,
      maintainanceMargin: 2.5,
    },
    {
      symbol: 'COOKIEUSDT',
      epsilon: 4,
      lotSize: 10,
      lotUnit: 'COOKIE',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/31838.png',
      leverage: 20,
      maintainanceMargin: 2.5,
    },
    {
      symbol: 'SOLVUSDT',
      epsilon: 4,
      lotSize: 100,
      lotUnit: 'SOLV',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/12165.png',
      leverage: 20,
      maintainanceMargin: 2.5,
    },
    {
      symbol: 'MANAUSDT',
      epsilon: 4,
      lotSize: 1,
      lotUnit: 'MANA',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/1966.png',
      leverage: 20,
      maintainanceMargin: 2.5,
    },
    {
      symbol: 'PAXGUSDT',
      epsilon: 2,
      lotSize: 0.001,
      lotUnit: 'PAXG',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/4705.png',
      leverage: 100,
      maintainanceMargin: 1,
    },
  ];

  getFuturesArrayByUnit(symbol: string): any {
    return this.futuresEpsilonArray.filter(
      (item: any) => item.symbol === symbol,
    );
  }

  getFuturesArray(): any {
    return this.futuresEpsilonArray;
  }
}
